import {SyncEventType} from '../types';

/**
 * Create a throttled time update function with play/pause variation.
 * This function is extracted for easier testing and reusability.
 *
 * When the video is paused, we allow more frequent updates for precise seeking.
 * When playing, we throttle to reduce event spam.
 */
export const createThrottledTimeUpdate = (
  publishEvent: (event: any) => void,
  isSyncEnabled: boolean,
  videoId: string,
  throttleMs: number = 100
) => {
  let lastUpdateTime = 0;

  return (currentTime: number, videoElement?: HTMLVideoElement) => {
    if (!isSyncEnabled) {
      return;
    }

    const now = performance.now();
    const timeSinceLastUpdate = now - lastUpdateTime;
    const isPaused = videoElement ? videoElement.paused : true;

    // No throttling when paused - allow all time updates for precise seeking
    // Normal throttling when playing to reduce event spam
    const shouldUpdate = isPaused || timeSinceLastUpdate >= throttleMs;

    if (shouldUpdate) {
      lastUpdateTime = now;

      const eventData = {
        type: SyncEventType.TIME_UPDATE,
        data: {currentTime},
        timestamp: now,
        videoId,
      };
      publishEvent(eventData);
    }
  };
};

/**
 * Create event handlers for a video element.
 * This function is extracted for easier testing and reusability.
 */
export const createEventHandlers = (
  video: HTMLVideoElement,
  videoId: string,
  handleVideoEvent: (
    eventType: SyncEventType,
    data?: any,
    shouldPause?: boolean
  ) => void,
  throttledTimeUpdate: (
    currentTime: number,
    videoElement?: HTMLVideoElement
  ) => void
) => {
  return {
    play: () => {
      if (videoId === 'unknown') {
        console.warn('video without id publishing play event');
        return;
      }
      // For ended videos, claim leadership and reset to beginning
      // Check both video.ended and if we're at the end (with small tolerance for floating point)
      const isAtEnd =
        video.ended || Math.abs(video.currentTime - video.duration) < 0.1;

      if (isAtEnd) {
        handleVideoEvent(SyncEventType.LEADER, {
          videoId: videoId,
          currentTime: 0,
          isPlaying: true,
          playbackRate: video.playbackRate,
        });
      } else {
        handleVideoEvent(SyncEventType.PLAY, {
          isPlaying: true,
          currentTime: video.currentTime,
          playbackRate: video.playbackRate,
        });
      }
    },
    pause: () => {
      // Use the browser's native end detection - it's authoritative
      if (video.ended) {
        handleVideoEvent(SyncEventType.PAUSE, {
          isPlaying: false,
          currentTime: video.currentTime,
          playbackRate: video.playbackRate,
          isEndOfVideo: true,
        });
      } else {
        handleVideoEvent(SyncEventType.PAUSE, {
          isPlaying: false,
          currentTime: video.currentTime,
          playbackRate: video.playbackRate,
        });
      }
    },
    seeking: () => {
      if (video.currentTime === video.duration) {
        return;
      }
      // User is scrubbing - this video should become the leader immediately
      handleVideoEvent(SyncEventType.LEADER, {
        videoId: videoId,
        currentTime: video.currentTime,
        playbackRate: video.playbackRate,
      });
    },
    seeked: () => {
      if (video.currentTime === video.duration) {
        return;
      }
      // Seeked completes the scrubbing - maintain leadership and update time
      handleVideoEvent(SyncEventType.SEEKED, {
        currentTime: video.currentTime,
        isPlaying: !video.paused,
        playbackRate: video.playbackRate,
      });
    },
    ratechange: () =>
      handleVideoEvent(SyncEventType.RATE_CHANGE, {
        playbackRate: video.playbackRate,
        currentTime: video.currentTime,
        isPlaying: !video.paused,
      }),
    timeupdate: () => throttledTimeUpdate(video.currentTime, video),
    mouseenter: () => {
      // prevent ended videos from claiming leadership
      const isAtEnd = video.currentTime === video.duration;
      const hasEnded = video.ended || isAtEnd;

      if (!hasEnded) {
        handleVideoEvent(SyncEventType.LEADER, {
          videoId: videoId,
          currentTime: video.currentTime,
          isPlaying: !video.paused,
          playbackRate: video.playbackRate,
        });
      }
    },
    ended: () => {
      if (video.currentTime !== video.duration) {
        video.currentTime = video.duration;
      }
      handleVideoEvent(SyncEventType.ENDED, {
        currentTime: video.currentTime,
        isPlaying: false,
        playbackRate: video.playbackRate,
      });
    },
  };
};
